import {
  BriefcaseMedical,
  Network,
  Pill,
  Upload,
  ClipboardList,
  Webhook,
} from 'lucide-react';
import { RetrievalMethod } from '@/feature/profiles/constants';

// Mapping for request methods to UI data
export const REQUEST_METHOD_CONFIG = {
  FULL: {
    value: RetrievalMethod.PROVIDER_FACILITY,
    icon: BriefcaseMedical,
    title: 'Provider/Facility Retrieval',
    description:
      'Retrieve records directly from care providers this patient has visited in the past',
    category: 'Records & Data',
    disabledReason: undefined,
  },
  HIE: {
    value: RetrievalMethod.HIE,
    icon: Network,
    title: 'Digital Health Data',
    description: 'Publicly available health data available by service ZIP Code',
    category: 'Records & Data',
    disabledReason:
      'An NPI Number is required for this retrieval type. Add one by editing the profile associated with this patient request.',
  },
  MEDICATIONS: {
    value: RetrievalMethod.MEDICATIONS,
    icon: Pill,
    title: 'Medications Data',
    description:
      'Pharmaceutical data including patients current/former medication history',
    category: 'Records & Data',
    disabledReason:
      'An NPI Number is required for this retrieval type. Add one by editing the profile associated with this patient request.',
  },
  RECORD_UPLOAD: {
    value: RetrievalMethod.RECORD_UPLOAD,
    icon: Upload,
    title: 'Medical Record Upload',
    description: 'Records in your possession that need to be uploaded',
    category: 'Records & Data',
    disabledReason: undefined,
  },
  FACESHEETS: {
    value: RetrievalMethod.FACESHEETS,
    icon: ClipboardList,
    title: 'Facesheets',
    description:
      'A clinical summary of key data points based on all available data',
    category: 'Analysis',
    disabledReason:
      'No facesheet templates have been configured for this profile. Please contact your administrator.',
  },
  PARTNER_API: {
    value: null, // Not a real retrieval method, only for recommendations
    icon: Webhook,
    title: 'Partner API',
    description:
      'Fully tailor your medical data retrieval and analysis needs to your business workflow with our partner API',
    category: 'Recommendations',
    disabledReason: undefined,
  },
} as const;

export type Suggestion = {
  key: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
};

// Helper function to get config for a method
export const getMethodConfig = (method: string) =>
  REQUEST_METHOD_CONFIG[method as keyof typeof REQUEST_METHOD_CONFIG];

// Helper functions to categorize methods
export const isRecordsAndDataMethod = (method: string) =>
  getMethodConfig(method)?.category === 'Records & Data';

export const isAnalysisMethod = (method: string) =>
  getMethodConfig(method)?.category === 'Analysis';
